import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "zbtv_favorites";

function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const sync = () => setFavorites(readFavorites());
    window.addEventListener("storage", sync);
    window.addEventListener("zbtv-favorites-change", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("zbtv-favorites-change", sync);
    };
  }, []);

  const persist = useCallback((next: string[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable */
    }
    setFavorites(next);
    window.dispatchEvent(new Event("zbtv-favorites-change"));
  }, []);

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  const toggleFavorite = useCallback(
    (id: string) => {
      const next = favorites.includes(id)
        ? favorites.filter((x) => x !== id)
        : [...favorites, id];
      persist(next);
      return next.includes(id);
    },
    [favorites, persist]
  );

  return { favorites, isFavorite, toggleFavorite };
}